/**
 * Label Performance Section Component
 *
 * Displays performance metrics for individual labels in a data table format.
 */

import { Heading, Text } from '@soteria/design-system-ve';
import { useMemo } from 'react';

import { DataList } from '../../../core/components';
import type { TableColumn } from '../../../core/components/DataList';

import * as styles from './LabelPerformanceSection.css';

interface LabelMetric {
  label: string;
  metrics: {
    precision: number;
    recall: number;
    f1Score: number;
    accuracy: number;
    labelCorrectnessCount?: number;
    labelCorrectnessPercentage?: number;
    policyCorrectnessCount?: number;
    policyCorrectnessPercentage?: number;
  };
}

interface LabelPerformanceSectionProps {
  labelMetrics: LabelMetric[];
}

export function LabelPerformanceSection({ labelMetrics }: LabelPerformanceSectionProps) {
  const columns: TableColumn<LabelMetric>[] = useMemo(
    () => [
      {
        key: 'label',
        header: 'Label',
        render: (item: LabelMetric) => <Text>{item.label}</Text>,
        width: '25%',
      },
      {
        key: 'precision',
        header: 'Precision',
        render: (item: LabelMetric) => <Text>{(item.metrics.precision * 100).toFixed(1)}%</Text>,
        width: '15%',
      },
      {
        key: 'recall',
        header: 'Recall',
        render: (item: LabelMetric) => <Text>{(item.metrics.recall * 100).toFixed(1)}%</Text>,
        width: '15%',
      },
      {
        key: 'f1Score',
        header: 'F1 Score',
        render: (item: LabelMetric) => <Text>{(item.metrics.f1Score * 100).toFixed(1)}%</Text>,
        width: '15%',
      },
      {
        key: 'accuracy',
        header: 'Accuracy',
        render: (item: LabelMetric) => <Text>{(item.metrics.accuracy * 100).toFixed(1)}%</Text>,
        width: '15%',
      },
      {
        key: 'labelCorrectness',
        header: 'Label Correctness',
        render: (item: LabelMetric) => (
          <Text>{item.metrics.labelCorrectnessPercentage !== undefined ? `${(item.metrics.labelCorrectnessPercentage * 100).toFixed(1)}%` : 'N/A'}</Text>
        ),
        width: '15%',
      },
    ],
    []
  );

  return (
    <div>
      <Heading level="h4">Performance by Label</Heading>
      <div className={styles.labelPerformanceSection}>
        <DataList hideEmptyState columns={columns} data={labelMetrics} dataTypeName="label metric" emptyStateMessage="No label metrics available." />
      </div>
    </div>
  );
}
